
import { FILE_STORAGE } from 'constants/routePaths';

import React, { useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';

import FileViewer from './FileViewer';
import FolderViewer from './FolderViewer';
import useStyles from './useStyles'; 
import selectSessionToken from '../../selectors/sessionToken';


// Sanitize path to prevent directory traversal attacks for API calls. Fix added to avoid cycode SAST warning.
const sanitizePathForApi=(path:string)=> {
  if(
    path.startsWith('/')||
    path.startsWith('./')||
    path.startsWith('../')
  )
  return '';
  const safe_path=/[^a-zA-Z0-9_\-.]+$/;
  return safe_path.test(path)? path:'';
};
//This component handles viewing files and folders based on the URL path.
const SlackView: React.FC = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const sessionToken = useSelector(selectSessionToken);

  // Extract the path from the URL: /file/test/test.pdf -> test/test.pdf
  const requestPath = useMemo(
    () => location.pathname.replace(/^\/file\//, '').replace(/^\/+/, ''),
    [location.pathname]
  );

  const [isFile, setIsFile] = useState<boolean | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Extract file name from path for display: test/test.pdf -> test.pdf
  const fileName = useMemo(
    () => (requestPath ? requestPath.split('/').pop() || 'download' : 'download'),
    [requestPath]
  );

  // This function detects whether the path is a file or folder.
  useEffect(() => {
    setError('');
    setLoading(true);
    setIsFile(null);

    if (!requestPath || !sessionToken) {
      setError('Missing path or authentication token');
      setLoading(false);
      return;
    }

    // This function checks for a file extension to quickly identify files.
    const hasExtension = /\.\w+$/.test(requestPath);
    if (hasExtension) {
      setIsFile(true);
      setLoading(false);
      return;
    }

    // This function calls the API to check if the path is a folder.
    const folderpath=requestPath.endsWith('/')? requestPath: requestPath+'/';
    const safePath=sanitizePathForApi(folderpath);
    if(!safePath){
      setError('Invalid path');
      setLoading(false);
      return;
    }
    const params = new URLSearchParams({ path: safePath });
    fetch(`/api/files? + params.toString()`, {
      headers: { Authorization: `Bearer ${sessionToken}` },
    })
      .then(async (res) => {
        if (res.status === 401) {
          setError('Unauthorized');
          setLoading(false);
          return;
        }

        if (res.ok) {
          // it is used to fetch folder contents
          await res.json();
          setIsFile(false);
          setLoading(false);
        } 
        else if (res.status === 404 || res.status === 403) {
          // Likely a file or blocked folder — default to file viewer
          setIsFile(true);
          setLoading(false);
        } 
        else {
          throw new Error(`Failed to detect path type: ${res.statusText}`);
        }
      })
      .catch((err) => {
        console.error('Error detecting path type:', err);
        // Default to file viewer on unexpected errors
        setIsFile(true);
        setLoading(false);
      });
  }, [requestPath, sessionToken]);

  // Deep links (e.g. from Slack) load with no keyed in-app entry, so goBack() would be a no-op or leave the app.
  const handleClose = () => {
    if (location.key) {
      history.goBack();
    } else {
      history.push(FILE_STORAGE);
    }
  };
  const handleNavigateToFile = (filePath: string) => history.push(`/file/${filePath}`);
  const handleNavigateToFolder = (folderPath: string) => history.push(`/file/${folderPath}`);

  // Shared header
  const Header = (
    <div className={classes.header}>
      <h3 className={classes.title}>
        {isFile ? fileName : (requestPath || 'root')}
      </h3>
      <button className={classes.closeButton} onClick={handleClose} aria-label="Close">
        Close
      </button>
    </div>
  );

  // Loading state
  if (loading || isFile === null) {
    return (
      <div className={classes.modalOverlay} role="dialog" aria-label="Viewer">
        {Header}
        <div className={classes.content}>
          <div>
            <div className={classes.loadingIcon}>⏳</div>
            <div className={classes.loadingFont}>Detecting path type...</div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={classes.modalOverlay} role="dialog" aria-label="Viewer">
        {Header}
        <div className={classes.content}>
          <div className={classes.fallbackStyle}>
            <div className={classes.error}>⚠️ {error}</div>
          </div>
        </div>
      </div>
    );
  }

  // File view
  if (isFile) {
    return (
      <div className={classes.modalOverlay} role="dialog" aria-label="File viewer">
        {Header}
        <div className={classes.content}>
          <FileViewer
            filePath={requestPath}
            fileName={fileName}
            onClose={handleClose}
          />
        </div>
      </div>
    );
  }

  // Folder view
  return (
    <div className={classes.modalOverlay} role="dialog" aria-label="Folder viewer">
      {Header}
      <div className={classes.folderContent}>
        <FolderViewer
          folderPath={requestPath}
          onClose={handleClose}
          onNavigateToFile={handleNavigateToFile}
          onNavigateToFolder={handleNavigateToFolder}
        />
      </div>
    </div>
  );
};

export default SlackView;
